import React from "react";
import { useTranslation } from "react-i18next";
import type { Slot, PollInsights, VoteType } from "../../lib/insights/types";
import type { Participant } from "./ResultsTable";
import { formatDate, formatTime } from "../voting/utils/dateUtils";
import SlotResponsesList from "./SlotResponsesList";

interface SlotCardProps {
  slot: Slot;
  participants: Participant[];
  insights: PollInsights;
}

/**
 * Carte mobile par créneau : heure + scores + bar yes/maybe/no + grille
 * d'avatars avec glyph de vote. Cf. Results Final.html lignes 488-545.
 */
const SlotCard: React.FC<SlotCardProps> = ({
  slot,
  participants,
  insights,
}) => {
  const { t } = useTranslation("polls");
  const isWinner = slot.id === insights.winnerSlotId;
  const score = insights.scores.find((s) => s.slotId === slot.id) ?? {
    yes: 0,
    maybe: 0,
    no: 0,
    empty: 0,
    total: 0,
    slotId: slot.id,
  };
  const total = insights.participantsCount;
  const empty = Math.max(0, total - score.yes - score.maybe - score.no);
  const timeLabel = slot.timeSlots ? formatTime(slot.timeSlots) : null;

  return (
    <div
      data-testid={`slot-card-${slot.id}`}
      data-winner={isWinner ? "true" : undefined}
      className={`relative rounded-xl border p-3 ${
        isWinner ? "border-success/70 bg-success/5" : "border-border bg-card"
      }`}
    >
      {isWinner && (
        <div className="absolute top-2 right-3 text-[9px] font-bold uppercase tracking-wide text-success">
          ★ {t("results_leading_badge")}
        </div>
      )}
      <div className="flex items-baseline gap-2 mb-2.5">
        <div className="text-[20px] font-semibold tabular-nums">
          {timeLabel ?? slot.groupLabel ?? formatDate(slot.primaryDate)}
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2 text-[11px] tabular-nums">
          <span className="text-success">
            <strong className="text-[13px]">{score.yes}</strong>{" "}
            {t("results_yes_label")}
          </span>
          {score.maybe > 0 && (
            <span className="text-warning">{score.maybe} ~</span>
          )}
          {score.no > 0 && (
            <span className="text-destructive">{score.no} non</span>
          )}
        </div>
      </div>

      {/* Vote-split bar */}
      <div
        data-testid={`slot-card-bar-${slot.id}`}
        className="flex h-[3px] rounded-[3px] overflow-hidden bg-muted/40 mb-2.5"
      >
        <div className="bg-success" style={{ flex: score.yes || 0 }} />
        <div className="bg-warning" style={{ flex: score.maybe || 0 }} />
        <div className="bg-destructive" style={{ flex: score.no || 0 }} />
        <div style={{ flex: empty || 0 }} />
      </div>

      {/* Avatars row */}
      <div className="flex gap-1 flex-wrap">
        {participants.map((p) => {
          const v: VoteType | undefined = p.votes[slot.id];
          const badgeBg =
            v === "yes"
              ? "bg-success"
              : v === "maybe"
                ? "bg-warning"
                : v === "no"
                  ? "bg-destructive"
                  : null;
          const badgeFg =
            v === "yes"
              ? "text-success-foreground"
              : v === "maybe"
                ? "text-warning-foreground"
                : v === "no"
                  ? "text-destructive-foreground"
                  : null;
          const glyph =
            v === "yes" ? "✓" : v === "maybe" ? "~" : v === "no" ? "✕" : "";
          return (
            <div
              key={p.id}
              title={p.name}
              data-testid={`slot-card-avatar-${slot.id}-${p.id}`}
              data-vote={v ?? "empty"}
              className={`relative h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-foreground bg-muted ${v ? "opacity-100" : "opacity-40"} ${p.isYou ? "ring-[1.5px] ring-foreground" : ""}`}
            >
              {(p.name || "?").charAt(0).toUpperCase()}
              {badgeBg && (
                <div
                  className={`absolute -bottom-0.5 -right-0.5 h-[11px] w-[11px] rounded-full ${badgeBg} text-[7px] font-bold flex items-center justify-center ${badgeFg} border-2 border-background`}
                  aria-hidden
                >
                  {glyph}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Responses list with full names */}
      <SlotResponsesList participants={participants} slotId={slot.id} />
    </div>
  );
};

export default SlotCard;
